import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { Subscription } from 'rxjs';
import { AppUtils } from '../../../../core/configs/app.utils';
import { SETTINGS } from '../../../../core/configs/common.settings';
import { ClientService } from '../../../../core/services/client.service';
import { CompanyService } from '../../../../core/services/company.service';
import { FilesService } from '../../../../core/services/files.service';
import { GooglePlaceApiService } from '../../../../core/services/google-place-api.service';
import { requiredMissing } from '../form-kit/required-missing';
import { PAYMENT_TERMS_OPTIONS } from './payment-terms.util';

export type ClientFormVariant = 'default' | 'lead';

const PHONE = Validators.pattern(SETTINGS.REGEX.COUNTRY_CODE_PHONE.EXP);

const abnAcnValidator: ValidatorFn = (control: AbstractControl) => {
  const raw = String(control.value || '').replace(/\s/g, '');
  if (!raw) {
    return null;
  }
  if (!/^\d+$/.test(raw) || (raw.length !== 11 && raw.length !== 9)) {
    return { abnAcn: true };
  }
  return null;
};
const BILLING_MIRRORED = ['firstname', 'lastname', 'email', 'mobileNumber', 'phoneNumber'];
const ADDRESS_FIELDS = ['unit', 'street', 'suburb', 'state', 'postalCode'];

/**
 * Client create/edit form in the job-form format. Used by the routed client page (`layout="page"`)
 * and by the add-client drawers (`layout="drawer"`). The `lead` variant relaxes contact requirements.
 */
@Component({
  selector: 'app-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.scss'],
})
export class ClientFormComponent implements OnInit, OnDestroy {
  @Input() layout: 'page' | 'drawer' = 'page';
  @Input() clientId: string | null = null;
  @Input() variant: ClientFormVariant = 'default';
  /** Preselected company when the topbar company is "All". */
  @Input() defaultCompanyId: string | null = null;
  /** Name typed in the caller's search box, used to prefill the name fields. */
  @Input() initialName = '';

  @Output() saved = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  renameForm!: FormGroup;

  loading = true;
  saving = false;
  submitted = false;
  /** True once the user has changed something that is not saved yet. */
  hasUnsavedChanges = false;

  companyOptions: { label: string; value: string }[] = [];
  readonly titleOptions = [
    { label: 'Mr.', value: 'mr' },
    { label: 'Mrs.', value: 'mrs' },
  ];
  /** CR-10: payment terms options for Billing Details. */
  readonly paymentTermsOptions = [...PAYMENT_TERMS_OPTIONS];

  showCompanyField = true;
  isSame = false;
  addressFetching = false;

  attachments: any[] = [];
  attachmentUploading = false;
  renameDialog = false;
  private renameIndex = 0;

  private clientData: any;
  private clientFirstProject: any;
  private projects: any[] = [];
  private addresses: any[] = [];
  private subs: Subscription[] = [];
  private billingSyncSubs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private companyService: CompanyService,
    private fileService: FilesService,
    private placeApiService: GooglePlaceApiService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  get isEdit(): boolean {
    return !!this.clientId;
  }

  get isLead(): boolean {
    return this.variant === 'lead';
  }

  get isBusiness(): boolean {
    return this.form?.get('type')?.value === 'BUSINESS';
  }

  get title(): string {
    return this.isEdit ? 'Edit client' : 'Add client';
  }

  get saveLabel(): string {
    return this.isEdit ? 'Save changes' : 'Add client';
  }

  get isActive(): boolean {
    return !!this.form?.get('active')?.value;
  }

  get missingFields(): string[] {
    const f = this.form;
    if (!f || this.loading) return [];
    const business = () => this.isBusiness;
    const personal = () => !!f.get('type')?.value && !this.isBusiness;
    const strict = () => !this.isLead;
    return requiredMissing(f, [
      { label: 'Company', control: 'company' },
      { label: 'Client type', control: 'type' },
      { label: 'Company name', control: 'companyName', when: business },
      { label: 'Title', control: 'title', when: () => personal() && strict() },
      { label: 'First name', control: 'firstname', when: personal },
      { label: 'Last name', control: 'lastname', when: personal },
      { label: 'Email', control: 'email', when: strict },
      { label: 'Mobile', control: 'mobileNumber', when: strict },
      { label: 'Contact first name', control: 'contactPerson.firstname', when: business },
      { label: 'Contact last name', control: 'contactPerson.lastname', when: business },
      { label: 'Contact email', control: 'contactPerson.email', when: () => business() && strict() },
      { label: 'Contact mobile', control: 'contactPerson.mobile', when: () => business() && strict() },
      { label: 'ABN / ACN', control: 'address.abn', when: () => business() && strict() },
      { label: 'Search address', control: 'address.street', when: strict },
    ]);
  }

  get canSave(): boolean {
    return !this.loading && !this.saving && this.missingFields.length === 0;
  }

  ngOnInit(): void {
    this.buildForm();
    this.renameForm = this.fb.group({
      fileName: ['', [Validators.required, this.fileNameValidator]],
      fileExtension: [{ value: '', disabled: true }],
    });

    this.subs.push(
      this.placeApiService.placeFetchingInfo.subscribe((res: any) => {
        if (res?.type === 'component') {
          this.addressFetching = !!res.value;
          this.setAddressEnabled(!res.value);
        }
      })
    );

    this.loadCompanies();
    this.refreshConditionalValidators();
    this.subs.push(
      this.form.get('type')!.valueChanges.subscribe(() => this.refreshConditionalValidators())
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.clearBillingSync();
  }

  // ---------------------------------------------------------------------------
  // Form
  // ---------------------------------------------------------------------------

  private buildForm(): void {
    const strict = !this.isLead;
    this.form = this.fb.group({
      company: [null, Validators.required],
      type: [null, Validators.required],
      companyName: [null, this.whenType('BUSINESS')],
      title: [null, strict ? this.whenType('PERSONAL') : []],
      firstname: [null, this.whenType('PERSONAL')],
      lastname: [null, this.whenType('PERSONAL')],
      email: [null, strict ? [Validators.required, Validators.email] : [Validators.email]],
      mobileNumber: [null, strict ? [Validators.required, PHONE] : [PHONE]],
      secondaryEmail: [null, Validators.email],
      phoneNumber: [null, PHONE],
      address: this.fb.group({
        unit: [null],
        street: [null],
        suburb: [null],
        state: [null],
        postalCode: [null],
        abn: [null],
        addressTitle: [SETTINGS.COMMON_WORDS.MAIN, Validators.required],
      }),
      contactPerson: this.fb.group({
        firstname: [null, this.whenType('BUSINESS')],
        lastname: [null, this.whenType('BUSINESS')],
        jobPosition: [null],
        email: [null, strict ? [this.whenType('BUSINESS'), Validators.email] : [Validators.email]],
        mobile: [null, strict ? [this.whenType('BUSINESS'), PHONE] : [PHONE]],
        phoneNumber: [null, PHONE],
      }),
      billingDetails: this.fb.group({
        firstname: [null],
        lastname: [null],
        email: [null, Validators.email],
        phoneNumber: [null, PHONE],
        mobileNumber: [null, PHONE],
        paymentTerms: [null],
        note: [null],
      }),
      billingAddress: this.fb.group({
        unit: [null],
        street: [null],
        suburb: [null],
        state: [null],
        postalCode: [null],
      }),
      project: [null],
      additionalNote: [null],
      active: [true],
    });

    this.subs.push(
      this.form.valueChanges.subscribe(() => {
        if (!this.loading) this.hasUnsavedChanges = true;
      })
    );
  }

  /** Required only while the client type matches. */
  private whenType(type: 'BUSINESS' | 'PERSONAL'): ValidatorFn {
    return (control: AbstractControl) => {
      const root = control.root as FormGroup;
      const current = root?.get?.('type')?.value;
      if (current === type && (control.value === null || control.value === '')) {
        return { required: true };
      }
      return null;
    };
  }

  private refreshConditionalValidators(): void {
    const strict = !this.isLead;
    const address = this.form.get('address') as FormGroup;
    const abn = address?.get('abn');
    const street = address?.get('street');
    if (this.isBusiness && strict) {
      abn?.setValidators([Validators.required, abnAcnValidator]);
    } else {
      abn?.setValidators(abnAcnValidator);
    }
    abn?.updateValueAndValidity({ emitEvent: false });
    if (strict) {
      street?.setValidators([Validators.required]);
    } else {
      street?.clearValidators();
    }
    street?.updateValueAndValidity({ emitEvent: false });
  }

  setType(type: 'BUSINESS' | 'PERSONAL'): void {
    if (this.form.get('type')?.value === type) return;
    this.form.patchValue({ type });
    this.form.get('contactPerson')?.reset();
    if (type === 'BUSINESS') {
      this.form.patchValue({ title: null, firstname: null, lastname: null });
      this.form.get('billingDetails')?.patchValue({ firstname: null, lastname: null });
    } else {
      this.form.patchValue({ companyName: null });
    }
    this.revalidateTypeFields();
    this.refreshConditionalValidators();
    this.applyInitialName();
    if (this.isSame) this.syncBilling();
  }

  private revalidateTypeFields(): void {
    ['companyName', 'title', 'firstname', 'lastname'].forEach(k =>
      this.form.get(k)?.updateValueAndValidity({ emitEvent: false })
    );
    Object.values((this.form.get('contactPerson') as FormGroup).controls).forEach(c =>
      c.updateValueAndValidity({ emitEvent: false })
    );
  }

  private applyInitialName(): void {
    const name = (this.initialName || '').trim();
    if (!name || this.isEdit) return;
    const type = this.form.get('type')?.value;
    const [first, ...rest] = name.split(/\s+/);
    const patch: any = {};
    if (type !== 'PERSONAL' && !this.form.get('companyName')?.value) patch.companyName = name;
    if (type !== 'BUSINESS') {
      if (!this.form.get('firstname')?.value) patch.firstname = first;
      if (!this.form.get('lastname')?.value && rest.length) patch.lastname = rest.join(' ');
    }
    this.form.patchValue(patch);
  }

  isInvalid(path: string): boolean {
    const c = this.form?.get(path);
    return !!c && c.invalid && (c.touched || this.submitted);
  }

  hasError(path: string, error: string): boolean {
    const c = this.form?.get(path);
    return !!c && !!c.errors?.[error] && (c.touched || this.submitted);
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  private loadCompanies(): void {
    this.companyService.getAllCompanies().subscribe(
      (res: any) => {
        this.companyOptions = (res || []).map((c: any) => ({ label: c.name, value: c._id }));
        if (this.isEdit) {
          this.loadClient();
        } else {
          const topbar = localStorage.getItem(SETTINGS.COMPANY);
          const company = topbar || this.defaultCompanyId;
          this.showCompanyField = !topbar;
          if (company) this.form.patchValue({ company });
          this.applyInitialName();
          this.finishLoading();
        }
      },
      (error: any) => {
        this.finishLoading();
        this.toastError(error);
      }
    );
  }

  private loadClient(): void {
    this.clientService.getOneClientById(this.clientId).subscribe(
      (res: any) => {
        if (res?.project) {
          this.clientFirstProject = res.project;
          delete res.project;
        }
        this.clientData = res;
        this.projects = res?.projects || [];
        this.addresses = [...(res?.address || [])];
        if (this.addresses[0]?.addressTitle !== SETTINGS.COMMON_WORDS.MAIN) {
          this.addresses.unshift(this.form.get('address')?.getRawValue());
        }
        const firstContact = (res?.contacts || []).find((c: any) => c.isFirstContact) || {};

        this.form.patchValue({
          ...res,
          company: res?.company?._id || res?.company || null,
          address: this.addresses[0],
          contactPerson: firstContact,
          project: this.clientFirstProject?.name ?? null,
        });
        this.revalidateTypeFields();

        this.isSame = !!res?.isSameDetails;
        if (this.isSame) this.enableBillingSync();

        const topbar = localStorage.getItem(SETTINGS.COMPANY);
        const clientCompany = res?.company?._id || res?.company;
        this.showCompanyField = !(topbar && topbar === clientCompany);
        if (topbar && topbar !== clientCompany) {
          this.messageService.add({
            severity: 'info',
            summary: 'Info',
            detail: `Selected company and selected client's company is different`,
          });
        }
        this.attachments = res?.attachments || [];
        this.finishLoading();
      },
      (error: any) => {
        this.finishLoading();
        this.toastError(error);
      }
    );
  }

  private finishLoading(): void {
    this.loading = false;
    this.hasUnsavedChanges = false;
  }

  // ---------------------------------------------------------------------------
  // Billing mirrors client details
  // ---------------------------------------------------------------------------

  onSameToggle(): void {
    if (this.isSame) {
      this.enableBillingSync();
      return;
    }
    this.clearBillingSync();
    const details = this.form.get('billingDetails') as FormGroup;
    BILLING_MIRRORED.forEach(k => details.get(k)?.enable({ emitEvent: false }));
    this.form.get('billingAddress')?.enable({ emitEvent: false });
    if (this.isEdit && !this.clientData?.isSameDetails) {
      this.form.patchValue({
        billingDetails: {
          firstname: this.clientData?.billingDetails?.firstname,
          lastname: this.clientData?.billingDetails?.lastname,
          email: this.clientData?.billingDetails?.email,
          phoneNumber: this.clientData?.billingDetails?.phoneNumber,
          mobileNumber: this.clientData?.billingDetails?.mobileNumber,
          paymentTerms: this.clientData?.billingDetails?.paymentTerms,
          note: this.clientData?.billingDetails?.note,
        },
        billingAddress: this.clientData?.billingAddress,
      });
    } else {
      BILLING_MIRRORED.forEach(k => details.get(k)?.reset());
      this.form.get('billingAddress')?.reset();
    }
  }

  private enableBillingSync(): void {
    this.syncBilling();
    const details = this.form.get('billingDetails') as FormGroup;
    BILLING_MIRRORED.forEach(k => details.get(k)?.disable({ emitEvent: false }));
    this.form.get('billingAddress')?.disable({ emitEvent: false });
    this.clearBillingSync();
    ['firstname', 'lastname', 'email', 'phoneNumber', 'mobileNumber', 'address'].forEach(k => {
      const c = this.form.get(k);
      if (c) this.billingSyncSubs.push(c.valueChanges.subscribe(() => this.syncBilling()));
    });
  }

  private clearBillingSync(): void {
    this.billingSyncSubs.forEach(s => s.unsubscribe());
    this.billingSyncSubs = [];
  }

  private syncBilling(): void {
    const v = this.form.getRawValue();
    const { abn, addressTitle, ...address } = v.address || {};
    this.form.patchValue(
      {
        billingDetails: {
          firstname: v.firstname,
          lastname: v.lastname,
          email: v.email,
          phoneNumber: v.phoneNumber,
          mobileNumber: v.mobileNumber,
        },
        billingAddress: address,
      },
      { emitEvent: false }
    );
  }

  // ---------------------------------------------------------------------------
  // Address
  // ---------------------------------------------------------------------------

  onLocationChange(event: any): void {
    if (event?.name !== 'component-address') return;
    const components = event.value?.address_components || [];
    const address = this.form.get('address') as FormGroup;
    const abn = address.get('abn')?.value;
    address.reset({ addressTitle: SETTINGS.COMMON_WORDS.MAIN, abn });
    const map: Record<string, string> = {
      unit: 'unit',
      route: 'street',
      locality: 'suburb',
      administrative_area_level_1: 'state',
      postal_code: 'postalCode',
    };
    components.forEach((c: any) => {
      const key = map[c?.types?.[0]];
      if (key) address.patchValue({ [key]: c.long_name });
    });
    this.placeApiService.placeFetchingInfo.next({ type: 'component', value: false });
  }

  private setAddressEnabled(enabled: boolean): void {
    const address = this.form?.get('address');
    if (!address) return;
    ADDRESS_FIELDS.forEach(k =>
      enabled ? address.get(k)?.enable({ emitEvent: false }) : address.get(k)?.disable({ emitEvent: false })
    );
  }

  // ---------------------------------------------------------------------------
  // Save / cancel
  // ---------------------------------------------------------------------------

  save(): void {
    this.submitted = true;
    if (this.form.invalid || this.missingFields.length) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Check the form',
        detail: 'Some fields are missing or invalid.',
      });
      return;
    }
    if (this.isEdit) {
      this.confirmationService.confirm({
        key: 'clientFormUpdate',
        message: 'Are you sure you want to save the changes?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.update(),
      });
    } else {
      this.create();
    }
  }

  private contactsFor(v: any, existing: any[] = []): any[] {
    if (v.type === 'PERSONAL') {
      return [
        {
          firstname: v.firstname,
          lastname: v.lastname,
          email: v.email,
          jobPosition: existing[0]?.jobPosition || null,
          phoneNumber: v.phoneNumber,
          mobile: v.mobileNumber,
          isFirstContact: true,
        },
      ];
    }
    return [{ ...v.contactPerson, isFirstContact: true }];
  }

  private create(): void {
    const v = this.form.getRawValue();
    if (v.type === 'PERSONAL') v.companyName = null;
    const contacts = this.contactsFor(v);
    if (v.project === null || v.project === '') {
      v.projects = null;
    } else {
      v.project = { name: v.project };
    }
    this.saving = true;
    this.clientService
      .createClient({ ...v, contacts, isSameDetails: this.isSame, attachments: this.attachments })
      .subscribe(
        (res: any) => {
          this.saving = false;
          this.hasUnsavedChanges = false;
          this.clientService.clientsChange.next(true);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'New client has been added!' });
          this.saved.emit(res);
        },
        (error: any) => {
          this.saving = false;
          this.toastError(error);
        }
      );
  }

  private update(): void {
    const v = this.form.getRawValue();
    if (v.type === 'PERSONAL') v.companyName = null;
    const contacts = [...(this.clientData?.contacts || [])];
    contacts[0] = this.contactsFor(v, contacts)[0];
    const addresses = [...this.addresses];
    addresses[0] = this.form.get('address')?.getRawValue();

    const projectName = v.project === '' ? null : v.project;
    const projects = [...this.projects];
    if (projects.length) {
      if (projectName === null) projects.splice(0, 1);
      else projects[0] = { ...projects[0], name: projectName };
    } else if (projectName !== null) {
      projects.push({ name: projectName, date: new Date() });
    }
    const clientUpdatedFirstProject = this.clientFirstProject
      ? { name: projectName, _id: this.clientFirstProject._id }
      : undefined;
    const clientNewFirstProject = !this.clientFirstProject && projectName ? { name: projectName } : undefined;
    delete v.project;

    this.saving = true;
    this.clientService
      .updateClient({
        _id: this.clientId,
        ...v,
        projects,
        contacts,
        address: addresses,
        isSameDetails: this.isSame,
        attachments: this.attachments,
        clientUpdatedFirstProject,
        clientNewFirstProject,
      })
      .subscribe(
        (res: any) => {
          this.saving = false;
          this.hasUnsavedChanges = false;
          this.clientService.clientsChange.next(true);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Client has been updated!' });
          this.saved.emit(res);
        },
        (error: any) => {
          this.saving = false;
          this.toastError(error);
        }
      );
  }

  cancel(): void {
    if (!this.hasUnsavedChanges) {
      this.cancelled.emit();
      return;
    }
    this.confirmationService.confirm({
      key: 'canDeactivateConfirmDialog',
      message: 'Are you sure you want to discard all unsaved changes?',
      icon: 'pi pi-exclamation-triangle',
      header: '',
      acceptLabel: 'Discard Changes',
      acceptIcon: 'pi pi-times',
      rejectLabel: 'Keep Editing',
      rejectIcon: 'pi pi-check',
      accept: () => {
        this.hasUnsavedChanges = false;
        this.cancelled.emit();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Attachments (saved immediately in edit mode)
  // ---------------------------------------------------------------------------

  onAttachmentUpload(event: any, uploader: FileUpload): void {
    const file: File | undefined = event?.files?.[0];
    if (!file) return;
    this.attachmentUploading = true;
    this.fileService.postFile(file).subscribe(
      (res: any) => {
        this.attachments = [...this.attachments, { originalFileName: file.name, url: res.url }];
        this.persistAttachments();
        this.attachmentUploading = false;
        uploader.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attachments have been added!' });
      },
      (error: any) => {
        this.attachmentUploading = false;
        uploader.clear();
        this.toastError(error);
      }
    );
  }

  removeAttachment(index: number): void {
    this.confirmationService.confirm({
      key: 'clientFormRemoveFile',
      message: 'Are you sure you want to delete this attachment?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.attachments = this.attachments.filter((_, i) => i !== index);
        this.persistAttachments();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attachment have been deleted!' });
      },
    });
  }

  openRename(index: number): void {
    this.renameIndex = index;
    const parts = String(this.attachments[index]?.originalFileName || '').split('.');
    const ext = parts.length > 1 ? parts.pop() : '';
    this.renameForm.reset({ fileName: parts.join('.'), fileExtension: ext });
    this.renameDialog = true;
  }

  saveRename(): void {
    if (this.renameForm.invalid) return;
    const { fileName, fileExtension } = this.renameForm.getRawValue();
    this.attachments = this.attachments.map((f, i) =>
      i === this.renameIndex
        ? { ...f, originalFileName: fileExtension ? `${fileName}.${fileExtension}` : fileName }
        : f
    );
    this.persistAttachments();
    this.renameDialog = false;
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attachment has been renamed!' });
  }

  private persistAttachments(): void {
    if (this.isEdit) {
      this.clientService.updateClient({ _id: this.clientId, attachments: this.attachments }).subscribe();
    } else {
      this.hasUnsavedChanges = true;
    }
  }

  fileIcon(name: string): string {
    return AppUtils.getUrlExtension(name);
  }

  private fileNameValidator = (control: FormControl) =>
    /[./]/.test(control.value || '') ? { invalidChars: true } : null;

  private toastError(error: any): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: error?.error?.message || 'Error',
    });
  }
}
